// INTELIMOVE-JRN-004 — Camada de jornadas: contratos, definição versionada da J4 e motor PURO de derivação
// (IM-ADR-013). Sem banco, sem rede, sem IA, sem fila, sem relógio global: toda data de referência é explícita (`asOf`).
// Princípios (ARQUITETURA_INCREMENTAL_JORNADAS.md, MAQUINA_ESTADOS_JORNADAS.md):
//   • Os módulos continuam donos de dados, regras, execuções, versões e decisões. A jornada só LÊ (leitores, JRN-006)
//     e DERIVA aqui o estado das etapas, o progresso, as pendências e a próxima ação.
//   • A próxima ação leva à tela existente (deep link); a jornada nunca executa capacidade.
//   • Persistido é só o ciclo de vida da instância e as exceções humanas por etapa (`step_overrides`, JRN-005).
//   • Nenhuma função altera o argumento recebido; a definição é imutável (congelada) e versionada.
import { z } from 'zod';
import { EXEC_MODULE_LABEL } from './executive.js';

// ── Vocabulários ─────────────────────────────────────────────────────────────────────────────────────────────────
export const JOURNEY_TYPES = ['PLANEJAMENTO_ESTRATEGICO', 'CONCORRENTES', 'CARTEIRA_ALVOS', 'PORTFOLIO_POSICIONAMENTO', 'ACOMPANHAMENTO_PLANO'] as const;
export type JourneyType = (typeof JOURNEY_TYPES)[number];
/** Número `n` dos identificadores `Jn-mm` (ordem de IM-JRN-001..005). */
export const JOURNEY_NUMBER: Record<JourneyType, 1 | 2 | 3 | 4 | 5> = {
  PLANEJAMENTO_ESTRATEGICO: 1, CONCORRENTES: 2, CARTEIRA_ALVOS: 3, PORTFOLIO_POSICIONAMENTO: 4, ACOMPANHAMENTO_PLANO: 5,
};
export type StepId = `J${1 | 2 | 3 | 4 | 5}-${string}`;
export const STEP_ID_RE = /^J[1-5]-\d{2}$/;
export const isStepId = (x: unknown): x is StepId => typeof x === 'string' && STEP_ID_RE.test(x);

/** Ciclo de vida PERSISTIDO da instância (`journeys.lifecycle`, MAQUINA §2). Criar = iniciar: nasce `EM_ANDAMENTO`. */
export const JOURNEY_LIFECYCLES = ['EM_ANDAMENTO', 'PAUSADA', 'CONCLUIDA', 'EM_ACOMPANHAMENTO', 'CANCELADA', 'ARQUIVADA'] as const;
export type JourneyLifecycle = (typeof JOURNEY_LIFECYCLES)[number];
/** Persistidos que prevalecem sobre qualquer derivação (MAQUINA §3.1): nunca mascarados. */
export const JOURNEY_SETTLED_LIFECYCLES: ReadonlySet<JourneyLifecycle> = new Set(['PAUSADA', 'CONCLUIDA', 'EM_ACOMPANHAMENTO', 'CANCELADA', 'ARQUIVADA']);
/** Estados do TIPO no catálogo, enquanto não há instância viva (derivados). */
export const JOURNEY_CATALOG_STATES = ['NAO_INICIADA', 'PRONTA_PARA_COMECAR'] as const;
/** Refinamento DERIVADO de uma instância `EM_ANDAMENTO`, em ordem de precedência (MAQUINA §3.1). */
export const JOURNEY_ACTIVE_STATES = ['BLOQUEADA', 'AGUARDANDO_DECISAO', 'AGUARDANDO_REVISAO', 'AGUARDANDO_DADO', 'EM_ANDAMENTO'] as const;
/** Os 12 estados exibidos da jornada (MAQUINA §1). */
export const JOURNEY_STATES = [
  'NAO_INICIADA', 'PRONTA_PARA_COMECAR', 'EM_ANDAMENTO', 'AGUARDANDO_DADO', 'AGUARDANDO_REVISAO', 'AGUARDANDO_DECISAO', 'BLOQUEADA',
  'PAUSADA', 'CONCLUIDA', 'EM_ACOMPANHAMENTO', 'CANCELADA', 'ARQUIVADA',
] as const;
export type JourneyState = (typeof JOURNEY_STATES)[number];
export const JOURNEY_STATE_LABEL: Record<JourneyState, string> = {
  NAO_INICIADA: 'Não iniciada', PRONTA_PARA_COMECAR: 'Pronta para começar', EM_ANDAMENTO: 'Em andamento', AGUARDANDO_DADO: 'Aguardando dado',
  AGUARDANDO_REVISAO: 'Aguardando revisão', AGUARDANDO_DECISAO: 'Aguardando decisão', BLOQUEADA: 'Bloqueada', PAUSADA: 'Pausada',
  CONCLUIDA: 'Concluída', EM_ACOMPANHAMENTO: 'Em acompanhamento', CANCELADA: 'Cancelada', ARQUIVADA: 'Arquivada',
};

/** Estados DERIVADOS da etapa (MAQUINA §3.2). Só `DISPENSADA` tem origem persistida (exceção humana ou padrão da definição). */
export const STEP_STATES = [
  'NAO_INICIADA', 'PRONTA_PARA_COMECAR', 'EM_ANDAMENTO', 'AGUARDANDO_DADO', 'AGUARDANDO_REVISAO', 'AGUARDANDO_DECISAO', 'BLOQUEADA',
  'CONCLUIDA', 'CONCLUIDA_COM_LIMITACOES', 'DESATUALIZADA', 'DISPENSADA',
] as const;
export type StepState = (typeof STEP_STATES)[number];
export const STEP_STATE_LABEL: Record<StepState, string> = {
  NAO_INICIADA: 'Não iniciada', PRONTA_PARA_COMECAR: 'Pronta para começar', EM_ANDAMENTO: 'Em andamento', AGUARDANDO_DADO: 'Aguardando dado',
  AGUARDANDO_REVISAO: 'Aguardando revisão', AGUARDANDO_DECISAO: 'Aguardando decisão', BLOQUEADA: 'Bloqueada', CONCLUIDA: 'Concluída',
  CONCLUIDA_COM_LIMITACOES: 'Concluída com limitações', DESATUALIZADA: 'Desatualizada', DISPENSADA: 'Dispensada',
};
/** Contam como concluídas para o progresso (DESATUALIZADA com aviso — regra 10). */
export const STEP_DONE_STATES: ReadonlySet<StepState> = new Set(['CONCLUIDA', 'CONCLUIDA_COM_LIMITACOES', 'DESATUALIZADA']);
/** Satisfazem uma dependência. */
const SATISFIES_DEPENDENCY: ReadonlySet<StepState> = new Set(['CONCLUIDA', 'CONCLUIDA_COM_LIMITACOES', 'DESATUALIZADA', 'DISPENSADA']);
/** Permitidos para concluir a jornada (MAQUINA §4: DESATUALIZADA não conclui; DISPENSADA só onde a definição permite). */
const COMPLETES_JOURNEY: ReadonlySet<StepState> = new Set(['CONCLUIDA', 'CONCLUIDA_COM_LIMITACOES']);

/** Motivo estruturado do estado derivado (a tela traduz por `STEP_REASON_LABEL`). */
export const STEP_REASONS = [
  'DISPENSADA_POR_PADRAO', 'DISPENSADA_PELO_USUARIO', 'DEPENDENCIA_BLOQUEADA', 'DEPENDENCIA_PENDENTE', 'EXECUCAO_EM_ANDAMENTO', 'EXECUCAO_FALHOU',
  'LEITURA_INDISPONIVEL', 'DADO_AUSENTE', 'REVISAO_PENDENTE', 'DECISAO_PENDENTE', 'RESULTADO_ATUAL', 'RESULTADO_PARCIAL', 'CONFIRMADA_MANUALMENTE',
  'RESULTADO_DESATUALIZADO', 'MUDANCA_A_MONTANTE', 'RESULTADO_INCOMPATIVEL', 'CAPACIDADE_INDISPONIVEL', 'AGUARDANDO_CONFIRMACAO', 'PRONTA',
  'CONDICAO_NAO_APLICAVEL',
] as const;
export type StepReason = (typeof STEP_REASONS)[number];
export const STEP_REASON_LABEL: Record<StepReason, string> = {
  DISPENSADA_POR_PADRAO: 'Dispensada por padrão: o InteliMove ainda não tem os dados desta etapa.', DISPENSADA_PELO_USUARIO: 'Dispensada com justificativa.',
  DEPENDENCIA_BLOQUEADA: 'Uma etapa anterior está bloqueada.', DEPENDENCIA_PENDENTE: 'Depende de uma etapa anterior ainda não concluída.',
  EXECUCAO_EM_ANDAMENTO: 'Há uma execução em andamento no módulo.', EXECUCAO_FALHOU: 'A última execução falhou e não há resultado posterior.',
  LEITURA_INDISPONIVEL: 'Não foi possível ler a situação no módulo agora.', DADO_AUSENTE: 'Falta um dado indispensável.',
  REVISAO_PENDENTE: 'Há itens aguardando revisão.', DECISAO_PENDENTE: 'Há decisão aberta que esta etapa exige.', RESULTADO_ATUAL: 'Resultado atual.',
  RESULTADO_PARCIAL: 'Resultado parcial, com lacunas declaradas.', CONFIRMADA_MANUALMENTE: 'Confirmada por uma pessoa.',
  RESULTADO_DESATUALIZADO: 'O resultado está fora do limite de atualidade.', MUDANCA_A_MONTANTE: 'Uma etapa anterior mudou depois deste resultado.',
  RESULTADO_INCOMPATIVEL: 'Há resultado, mas ele não corresponde ao escopo desta jornada.', CAPACIDADE_INDISPONIVEL: 'A capacidade necessária não está disponível.',
  AGUARDANDO_CONFIRMACAO: 'Confira na tela do módulo e confirme a etapa.', PRONTA: 'Pode começar.',
  CONDICAO_NAO_APLICAVEL: 'Não se aplica agora.',
};

// ── Atualidade (IM-JRN-109, DP-05) ───────────────────────────────────────────────────────────────────────────────
/**
 * Padrões de atualidade DA LEITURA DA JORNADA, em dias. Origem: limites "propostos" da POLITICA_REAPROVEITAMENTO_ANALISES §2,
 * adotados pela DP-05 (IM-ADR-013) só para a jornada — continuam sendo DECISÃO DE PRODUTO revisável, não verdade universal,
 * e nunca alteram o comportamento dos módulos. Limite do módulo, quando existe, sempre prevalece.
 */
export const JOURNEY_FRESHNESS_DAYS = {
  PESQUISA_CONCORRENTE: 90, PESQUISA_EMPRESA_ALVO: 90, ANALISE_MERCADO: 90, LOTE_SUGESTOES: 30, MAPA_COMERCIAL: 30,
} as const;
export type JourneyFreshnessKey = keyof typeof JOURNEY_FRESHNESS_DAYS;
export const JOURNEY_FRESHNESS_POLICY = { nature: 'DECISAO_DE_PRODUTO', decidedBy: 'DP-05 (IM-ADR-013)', appliesTo: 'somente a leitura da jornada', modulePrevails: true } as const;
export const FRESHNESS_STATUSES = ['ATUAL', 'DESATUALIZADO', 'DESCONHECIDO', 'NAO_APLICAVEL'] as const;
export type FreshnessStatus = (typeof FRESHNESS_STATUSES)[number];
export interface Freshness {
  status: FreshnessStatus;
  /** Data da fonte, preservada como veio do módulo. */
  at: string | null;
  limitDays: number | null;
  limitSource: 'MODULO' | 'CAMADA' | null;
  ageDays: number | null;
}

// ── Definição (dados em código, versionados — ARQUITETURA §4) ────────────────────────────────────────────────────
/** Leitores previstos (CONTRATOS_ORQUESTRACAO §3); implementados na JRN-006 sobre funções exportadas dos módulos donos. */
export const JOURNEY_READERS = [
  'context.offerings', 'context.icpOfferings', 'battlecards.positioning', 'portfolio.current', 'portfolio.prices', 'portfolio.demand',
  'central.byOrigin', 'learning.byOffering',
  // J2 (INTELIMOVE-JRN-010): leituras enxutas de concorrentes, mercado, battlecards, fontes, sinais e Central.
  'competitors.registry', 'market.candidates', 'competitors.identity', 'competitors.classification', 'competitors.research', 'battlecards.dossier',
  'battlecards.review', 'battlecards.monitoringSuggestions', 'sources.competitorMonitoring', 'sources.competitorSocial', 'signals.competitorChanges',
  'battlecards.alerts', 'signals.materialChanges', 'central.competitorDecisions',
  // JRN-011: comparação da J2 com a prontidão de battlecards e de segmentação dos módulos donos.
  'battlecards.comparison',
] as const;
export type JourneyReader = (typeof JOURNEY_READERS)[number];
export type CapabilityId = `IM-CAP-${string}`;
export type SharedStepId = `EC-${string}`;
/** Rota de tela existente. `:id` é preenchido com o artefato vigente da etapa; sem artefato, vale `fallback`. */
export interface JourneyRoute { readonly path: string; readonly query?: Readonly<Record<string, string>>; readonly fallback?: JourneyRoute }
export interface JourneyStepAction { readonly label: string; readonly route: JourneyRoute }
/**
 * Como a etapa conclui: `ARTEFATO` (resultado do módulo), `CONFIRMACAO` (pessoa confirma depois de ler), `ARTEFATO_OU_CONFIRMACAO`,
 * `LACUNA` (capacidade inexistente no produto: só pode ficar dispensada).
 */
export type StepCompletion = 'ARTEFATO' | 'CONFIRMACAO' | 'ARTEFATO_OU_CONFIRMACAO' | 'LACUNA';
export interface JourneyStepDefinition {
  readonly id: StepId;
  readonly title: string;
  /**
   * Tipo da especificação: O (obrigatória), Op (opcional) ou C (condicional — JRN-010). A condicional conta como obrigatória
   * enquanto a condição se aplica; quando o leitor informa que não se aplica (`StepReading.notApplicable`), fica DISPENSADA
   * automaticamente com o motivo (FLUXOS §4: "as condicionais só contam quando a condição se aplica").
   */
  readonly kind: 'OBRIGATORIA' | 'OPCIONAL' | 'CONDICIONAL';
  /** `ACOMPANHAMENTO` = pós-conclusão (ex.: J4-12): não conta para progresso nem para concluir. */
  readonly phase: 'CONDUCAO' | 'ACOMPANHAMENTO';
  readonly dependsOn: readonly StepId[];
  readonly skippable: boolean;
  /** Dispensa automática da definição (lacuna declarada). */
  readonly defaultSkip: { readonly reason: string } | null;
  readonly completion: StepCompletion;
  /** Decisões abertas ligadas à etapa impedem a conclusão (regra 8). */
  readonly requiresDecision: boolean;
  readonly reader: JourneyReader | null;
  readonly capabilities: readonly CapabilityId[];
  readonly shared: readonly SharedStepId[];
  /** `MODULO`: só o limite informado pelo módulo; chave: padrão da camada quando o módulo não tem; `null`: não se aplica. */
  readonly freshness: 'MODULO' | JourneyFreshnessKey | null;
  readonly input: string;
  readonly output: string;
  readonly doneWhen: string;
  readonly action: JourneyStepAction | null;
}
export interface JourneyDefinition {
  readonly type: JourneyType;
  readonly version: number;
  readonly requirement: string;
  readonly title: string;
  readonly objective: string;
  /** Condição de entrada: a etapa indicada sem dado indispensável ausente. */
  readonly entry: { readonly stepId: StepId; readonly description: string };
  readonly completion: string;
  /** Quando a jornada passa a `EM_ACOMPANHAMENTO` (transição aplicada pelo serviço — JRN-005). */
  readonly tracking: { readonly when: 'DECISAO_APROVADA' | 'SEMPRE' | 'NUNCA'; readonly description: string };
  readonly steps: readonly JourneyStepDefinition[];
}

function deepFreeze<T>(x: T): T {
  if (x && typeof x === 'object' && !Object.isFrozen(x)) {
    Object.freeze(x);
    for (const v of Object.values(x as Record<string, unknown>)) deepFreeze(v);
  }
  return x;
}

const PF_ANALYSIS: JourneyRoute = { path: '/portfolio/:id', fallback: { path: '/portfolio' } };
/**
 * J4 — Rever portfólio, ofertas e posicionamento (IM-JRN-004), versão 1. Etapas, dependências, critérios e rotas de
 * ESPECIFICACAO_TECNICA_JORNADAS §IM-JRN-004.5, MATRIZ_JORNADAS_CAPACIDADES (J4) e CONTRATOS_ORQUESTRACAO §3.
 */
export const J4_PORTFOLIO_V1: JourneyDefinition = /*#__PURE__*/ deepFreeze<JourneyDefinition>({
  type: 'PORTFOLIO_POSICIONAMENTO', version: 1, requirement: 'IM-JRN-004',
  title: 'Rever portfólio, ofertas e posicionamento',
  objective: 'Avaliar se as ofertas cobrem ICPs e segmentos, como se comparam a concorrentes e substitutos (inclusive preço), que evidência de demanda existe, e decidir priorização, manutenção, combinação, reposicionamento ou descontinuação.',
  entry: { stepId: 'J4-01', description: 'Contexto confirmado com pelo menos uma oferta.' },
  completion: 'Análise de portfólio atual, preços do escopo verificados ou dispensados e recomendações que pedem decisão agora decididas na Central.',
  tracking: { when: 'DECISAO_APROVADA', description: 'Passa a acompanhamento quando houver decisão aprovada.' },
  steps: [
    { id: 'J4-01', title: 'Recuperar catálogo existente', kind: 'OBRIGATORIA', phase: 'CONDUCAO', dependsOn: [], skippable: false, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: false, reader: 'context.offerings', capabilities: ['IM-CAP-005', 'IM-CAP-032'], shared: ['EC-01'], freshness: 'MODULO',
      input: 'Ofertas', output: 'Catálogo de ofertas confirmado', doneWhen: 'Ofertas confirmadas; itens do catálogo externo vinculados ou ignorados.',
      action: { label: 'Abrir produtos e serviços', route: { path: '/configuracoes/contexto/produtos' } } },
    { id: 'J4-02', title: 'Relacionar ofertas a ICPs e segmentos', kind: 'OBRIGATORIA', phase: 'CONDUCAO', dependsOn: ['J4-01'], skippable: false, defaultSkip: null,
      completion: 'ARTEFATO_OU_CONFIRMACAO', requiresDecision: false, reader: 'context.icpOfferings', capabilities: ['IM-CAP-005', 'IM-CAP-015', 'IM-CAP-019'], shared: ['EC-02', 'EC-03'], freshness: 'MODULO',
      input: 'ICPs e segmentos', output: 'Matriz oferta × ICP', doneWhen: 'Nenhuma oferta sem ICP, ou oferta sem ICP confirmada com justificativa.',
      action: { label: 'Abrir ICPs', route: { path: '/configuracoes/contexto/icps' } } },
    { id: 'J4-03', title: 'Relacionar ofertas aos concorrentes', kind: 'OBRIGATORIA', phase: 'CONDUCAO', dependsOn: ['J4-01'], skippable: false, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: false, reader: 'battlecards.positioning', capabilities: ['IM-CAP-014', 'IM-CAP-019'], shared: [], freshness: 'MODULO',
      input: 'Battlecards dos concorrentes confirmados', output: 'Papéis de concorrência por oferta', doneWhen: 'Papéis de concorrência por oferta decididos.',
      action: { label: 'Abrir posicionamento competitivo', route: { path: '/battlecards', query: { aba: 'posicionamento' } } } },
    { id: 'J4-04', title: 'Identificar cobertura, lacunas e sobreposição', kind: 'OBRIGATORIA', phase: 'CONDUCAO', dependsOn: ['J4-02', 'J4-03'], skippable: false, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: false, reader: 'portfolio.current', capabilities: ['IM-CAP-019'], shared: [], freshness: 'MODULO',
      input: 'Matriz oferta × ICP e papéis de concorrência', output: 'Análise de portfólio', doneWhen: 'Análise de portfólio atual.',
      action: { label: 'Abrir análise de portfólio', route: { path: '/portfolio/:id', fallback: { path: '/portfolio', query: { nova: '1' } } } } },
    { id: 'J4-05', title: 'Recuperar preços disponíveis', kind: 'OPCIONAL', phase: 'CONDUCAO', dependsOn: ['J4-01'], skippable: true, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: false, reader: 'portfolio.prices', capabilities: ['IM-CAP-019'], shared: [], freshness: 'MODULO',
      input: 'Páginas de preço e informações da equipe', output: 'Preços com fonte', doneWhen: 'Preços do escopo decididos ou verificados.',
      action: { label: 'Abrir preços', route: { path: '/portfolio/:id', query: { aba: 'precos' }, fallback: { path: '/portfolio' } } } },
    { id: 'J4-06', title: 'Avaliar evidências de demanda', kind: 'OPCIONAL', phase: 'CONDUCAO', dependsOn: ['J4-04'], skippable: true, defaultSkip: null,
      completion: 'CONFIRMACAO', requiresDecision: false, reader: 'portfolio.demand', capabilities: ['IM-CAP-010', 'IM-CAP-015', 'IM-CAP-016', 'IM-CAP-031'], shared: [], freshness: 'MODULO',
      input: 'Atratividade, TAM/SAM/SOM, sinais e resultados', output: 'Leitura de demanda por oferta', doneWhen: 'Leitura de demanda por oferta conferida.',
      action: { label: 'Abrir análise de portfólio', route: PF_ANALYSIS } },
    { id: 'J4-07', title: 'Avaliar capacidade de entrega', kind: 'OPCIONAL', phase: 'CONDUCAO', dependsOn: [], skippable: true,
      defaultSkip: { reason: 'O InteliMove ainda não recebe dados operacionais de capacidade de entrega (previsto para a Onda 4).' },
      completion: 'LACUNA', requiresDecision: false, reader: null, capabilities: [], shared: [], freshness: null,
      input: 'Dados operacionais', output: '—', doneWhen: '—', action: null },
    { id: 'J4-08', title: 'Recomendar priorização', kind: 'OBRIGATORIA', phase: 'CONDUCAO', dependsOn: ['J4-04'], skippable: false, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: false, reader: 'portfolio.current', capabilities: ['IM-CAP-019'], shared: [], freshness: 'MODULO',
      input: 'Análise de portfólio', output: 'Recomendações priorizadas', doneWhen: 'Recomendações com prioridade geradas.',
      action: { label: 'Abrir análise de portfólio', route: PF_ANALYSIS } },
    { id: 'J4-09', title: 'Recomendar manter, combinar, reposicionar ou descontinuar', kind: 'OBRIGATORIA', phase: 'CONDUCAO', dependsOn: ['J4-08'], skippable: false, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: false, reader: 'portfolio.current', capabilities: ['IM-CAP-019'], shared: [], freshness: 'MODULO',
      input: 'Análise de portfólio', output: 'Recomendação por oferta', doneWhen: 'Recomendações por oferta presentes.',
      action: { label: 'Abrir análise de portfólio', route: PF_ANALYSIS } },
    { id: 'J4-10', title: 'Propor ofertas de entrada e pacotes', kind: 'OPCIONAL', phase: 'CONDUCAO', dependsOn: ['J4-09'], skippable: true,
      defaultSkip: { reason: 'O InteliMove ainda não gera recomendações de oferta de entrada e de pacote.' },
      completion: 'LACUNA', requiresDecision: false, reader: null, capabilities: [], shared: [], freshness: null,
      input: 'Ofertas e preços', output: '—', doneWhen: '—', action: null },
    { id: 'J4-11', title: 'Registrar decisão', kind: 'OBRIGATORIA', phase: 'CONDUCAO', dependsOn: ['J4-08', 'J4-09', 'J4-10'], skippable: false, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: true, reader: 'central.byOrigin', capabilities: ['IM-CAP-019', 'IM-CAP-029', 'IM-CAP-030'], shared: ['EC-08', 'EC-09'], freshness: null,
      input: 'Recomendações do portfólio', output: 'Decisões na Central', doneWhen: 'Recomendações que pedem decisão agora decididas na Central.',
      action: { label: 'Abrir a Central de decisões', route: { path: '/decisoes', query: { origin: EXEC_MODULE_LABEL.PORTFOLIO } } } },
    { id: 'J4-12', title: 'Acompanhar os efeitos', kind: 'OBRIGATORIA', phase: 'ACOMPANHAMENTO', dependsOn: ['J4-11'], skippable: false, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: false, reader: 'learning.byOffering', capabilities: ['IM-CAP-031'], shared: ['EC-11'], freshness: null,
      input: 'Decisões', output: 'Efeito por oferta', doneWhen: 'Acompanhamento contínuo depois da conclusão.',
      action: { label: 'Abrir resultados e aprendizado', route: { path: '/aprendizado' } } },
  ],
});

const COMPETITORS: JourneyRoute = { path: '/configuracoes/contexto/concorrentes' };
/**
 * J2 — Conhecer e acompanhar concorrentes (IM-JRN-002), versão 1 (INTELIMOVE-JRN-010). Etapas, títulos, ordem, tipos,
 * dependências e "pular" de ESPECIFICACAO_TECNICA_JORNADAS §IM-JRN-002.5; capacidades, rotas e reuso de
 * MATRIZ_JORNADAS_CAPACIDADES (J2). Precisões (registradas em CONTRATOS §8): "J2-01 ou J2-02" vira dependência só de J2-01,
 * porque candidato promovido na análise de mercado entra no mesmo cadastro que J2-01 conta; J2-11, J2-14 e J2-15 são
 * condicionais e ficam DISPENSADAS automaticamente quando a condição não se aplica (spec §9: rede social sem autorização).
 * A jornada nunca dispara pesquisa, coleta, IA ou job: cada etapa abre a tela existente do módulo.
 */
export const J2_CONCORRENTES_V1: JourneyDefinition = /*#__PURE__*/ deepFreeze<JourneyDefinition>({
  type: 'CONCORRENTES', version: 1, requirement: 'IM-JRN-002',
  title: 'Conhecer e acompanhar concorrentes',
  objective: 'Identificar concorrentes reais, conhecê-los com evidência, comparar-se a eles e acompanhar mudanças que exijam resposta.',
  entry: { stepId: 'J2-01', description: 'Contexto da empresa confirmado.' },
  completion: 'Cada concorrente do escopo classificado; os confirmados com battlecard ativo sem revisão de conflito ou alto impacto pendente e ao menos uma fonte monitorada.',
  tracking: { when: 'SEMPRE', description: 'Ao concluir, passa a acompanhar continuamente as fontes e os sinais dos concorrentes confirmados.' },
  steps: [
    { id: 'J2-01', title: 'Informar concorrentes conhecidos', kind: 'OBRIGATORIA', phase: 'CONDUCAO', dependsOn: [], skippable: false, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: false, reader: 'competitors.registry', capabilities: ['IM-CAP-005', 'IM-CAP-011'], shared: ['EC-01'], freshness: null,
      input: 'Nome, site ou CNPJ', output: 'Concorrentes cadastrados', doneWhen: 'Ao menos um concorrente cadastrado.',
      action: { label: 'Abrir concorrentes', route: COMPETITORS } },
    { id: 'J2-02', title: 'Descobrir candidatos', kind: 'OPCIONAL', phase: 'CONDUCAO', dependsOn: [], skippable: true, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: false, reader: 'market.candidates', capabilities: ['IM-CAP-012', 'IM-CAP-006'], shared: ['EC-05'], freshness: 'ANALISE_MERCADO',
      input: 'Análise de mercado', output: 'Candidatos promovidos ou descartados', doneWhen: 'Candidatos da análise de mercado promovidos ou descartados.',
      action: { label: 'Abrir análise de mercado', route: { path: '/mercado/:id', query: { aba: 'concorrentes' }, fallback: { path: '/mercado' } } } },
    { id: 'J2-03', title: 'Resolver identidade', kind: 'OBRIGATORIA', phase: 'CONDUCAO', dependsOn: ['J2-01'], skippable: false, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: false, reader: 'competitors.identity', capabilities: ['IM-CAP-011'], shared: ['EC-04'], freshness: null,
      input: 'Nome, site ou CNPJ', output: 'Empresa correta escolhida', doneWhen: 'Empresa identificada com segurança ou escolhida por uma pessoa.',
      action: { label: 'Abrir concorrentes', route: COMPETITORS } },
    { id: 'J2-04', title: 'Classificar (concorrente, substituto, hipótese)', kind: 'OBRIGATORIA', phase: 'CONDUCAO', dependsOn: ['J2-03'], skippable: false, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: true, reader: 'competitors.classification', capabilities: ['IM-CAP-011', 'IM-CAP-014'], shared: [], freshness: null,
      input: 'Pesquisa do concorrente', output: 'Classificação decidida', doneWhen: 'Cada concorrente classificado por uma pessoa (ou mantido como hipótese com justificativa).',
      action: { label: 'Abrir concorrentes', route: COMPETITORS } },
    { id: 'J2-05', title: 'Pesquisar site e fontes', kind: 'OBRIGATORIA', phase: 'CONDUCAO', dependsOn: ['J2-03'], skippable: false, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: false, reader: 'competitors.research', capabilities: ['IM-CAP-011'], shared: [], freshness: 'PESQUISA_CONCORRENTE',
      input: 'Identidade do concorrente', output: 'Pesquisa do concorrente', doneWhen: 'Pesquisa concluída e atual para cada concorrente.',
      action: { label: 'Abrir concorrentes', route: COMPETITORS } },
    { id: 'J2-06', title: 'Consolidar dossiê', kind: 'OBRIGATORIA', phase: 'CONDUCAO', dependsOn: ['J2-04', 'J2-05'], skippable: false, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: false, reader: 'battlecards.dossier', capabilities: ['IM-CAP-014'], shared: [], freshness: 'MODULO',
      input: 'Pesquisa, mercado e SWOT', output: 'Battlecard por concorrente confirmado', doneWhen: 'Battlecard ativo para cada concorrente confirmado.',
      action: { label: 'Abrir battlecards', route: { path: '/battlecards' } } },
    { id: 'J2-07', title: 'Comparar portfólio, mercados, ofertas e posicionamento', kind: 'OBRIGATORIA', phase: 'CONDUCAO', dependsOn: ['J2-06'], skippable: false, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: false, reader: 'battlecards.comparison', capabilities: ['IM-CAP-014', 'IM-CAP-019', 'IM-CAP-012'], shared: [], freshness: 'MODULO',
      input: 'Ofertas e battlecards', output: 'Papéis de concorrência por oferta', doneWhen: 'Papel de concorrência decidido para cada concorrente confirmado.',
      action: { label: 'Abrir posicionamento competitivo', route: { path: '/battlecards', query: { aba: 'posicionamento' } } } },
    { id: 'J2-08', title: 'Gerar ou atualizar battlecard (revisão por exceção)', kind: 'OBRIGATORIA', phase: 'CONDUCAO', dependsOn: ['J2-06'], skippable: false, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: false, reader: 'battlecards.review', capabilities: ['IM-CAP-014'], shared: [], freshness: 'MODULO',
      input: 'Pesquisa nova', output: 'Battlecard revisado', doneWhen: 'Nenhum item com conflito ou de alto impacto aguardando revisão.',
      action: { label: 'Abrir revisões do battlecard', route: { path: '/battlecards/:id', query: { aba: 'revisao' }, fallback: { path: '/battlecards' } } } },
    { id: 'J2-09', title: 'Descobrir fontes relacionadas', kind: 'OBRIGATORIA', phase: 'CONDUCAO', dependsOn: ['J2-05'], skippable: false, defaultSkip: null,
      completion: 'ARTEFATO_OU_CONFIRMACAO', requiresDecision: false, reader: 'battlecards.monitoringSuggestions', capabilities: ['IM-CAP-014', 'IM-CAP-008'], shared: ['EC-06'], freshness: null,
      input: 'Páginas lidas na pesquisa', output: 'Sugestões de monitoramento avaliadas', doneWhen: 'Sugestões avaliadas: ao menos uma fonte escolhida por concorrente confirmado, ou conferência registrada.',
      action: { label: 'Abrir monitoramento do battlecard', route: { path: '/battlecards/:id', query: { aba: 'monitoramento' }, fallback: { path: '/battlecards' } } } },
    { id: 'J2-10', title: 'Monitorar páginas', kind: 'OBRIGATORIA', phase: 'CONDUCAO', dependsOn: ['J2-09'], skippable: false, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: false, reader: 'sources.competitorMonitoring', capabilities: ['IM-CAP-009', 'IM-CAP-014'], shared: [], freshness: null,
      input: 'Endereços escolhidos', output: 'Fontes ativas por concorrente', doneWhen: 'Ao menos uma fonte ativa para cada concorrente confirmado.',
      action: { label: 'Abrir monitoramento', route: { path: '/monitoramento' } } },
    { id: 'J2-11', title: 'Acompanhar redes sociais', kind: 'CONDICIONAL', phase: 'CONDUCAO', dependsOn: [], skippable: true, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: false, reader: 'sources.competitorSocial', capabilities: ['IM-CAP-007'], shared: [], freshness: null,
      input: 'Conexão autorizada com a rede', output: 'Perfil social ativo', doneWhen: 'Perfil do concorrente ativo, quando houver conexão autorizada.',
      action: { label: 'Abrir fontes', route: { path: '/fontes' } } },
    { id: 'J2-12', title: 'Identificar mudanças', kind: 'OBRIGATORIA', phase: 'CONDUCAO', dependsOn: ['J2-10'], skippable: false, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: false, reader: 'signals.competitorChanges', capabilities: ['IM-CAP-009', 'IM-CAP-010'], shared: ['EC-07'], freshness: null,
      input: 'Coleta das fontes', output: 'Mudanças e sinais lidos', doneWhen: 'Mudanças e sinais novos dos concorrentes vistos.',
      action: { label: 'Abrir sinais', route: { path: '/sinais' } } },
    { id: 'J2-13', title: 'Gerar alertas', kind: 'OBRIGATORIA', phase: 'CONDUCAO', dependsOn: ['J2-12'], skippable: false, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: false, reader: 'battlecards.alerts', capabilities: ['IM-CAP-010', 'IM-CAP-014', 'IM-CAP-029'], shared: [], freshness: null,
      input: 'Mudanças', output: 'Alertas tratados', doneWhen: 'Nenhum alerta aberto: itens do battlecard desatualizados ou decisões sinalizadas para revisão.',
      action: { label: 'Abrir battlecards', route: { path: '/battlecards' } } },
    { id: 'J2-14', title: 'Recomendar resposta estratégica', kind: 'CONDICIONAL', phase: 'CONDUCAO', dependsOn: [], skippable: true, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: false, reader: 'signals.materialChanges', capabilities: ['IM-CAP-014', 'IM-CAP-010'], shared: [], freshness: null,
      input: 'Mudança material', output: 'Resposta proposta', doneWhen: 'Cada mudança material com tratamento proposto (ação registrada no sinal).',
      action: { label: 'Abrir sinais', route: { path: '/sinais' } } },
    { id: 'J2-15', title: 'Registrar decisão humana', kind: 'CONDICIONAL', phase: 'CONDUCAO', dependsOn: ['J2-14'], skippable: false, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: true, reader: 'central.competitorDecisions', capabilities: ['IM-CAP-029', 'IM-CAP-010'], shared: ['EC-08', 'EC-07'], freshness: null,
      input: 'Resposta proposta', output: 'Decisão registrada', doneWhen: 'Decisão humana registrada na Central ou no sinal, sem decisão aberta para agora.',
      action: { label: 'Abrir a Central de decisões', route: { path: '/decisoes', query: { origin: EXEC_MODULE_LABEL.BATTLECARD } } } },
    { id: 'J2-16', title: 'Acompanhar continuamente', kind: 'OBRIGATORIA', phase: 'ACOMPANHAMENTO', dependsOn: ['J2-10'], skippable: false, defaultSkip: null,
      completion: 'ARTEFATO', requiresDecision: false, reader: 'sources.competitorMonitoring', capabilities: ['IM-CAP-007', 'IM-CAP-025'], shared: [], freshness: null,
      input: 'Agenda das fontes', output: 'Coleta periódica', doneWhen: 'Acompanhamento contínuo depois da conclusão.',
      action: { label: 'Abrir fontes', route: { path: '/fontes' } } },
  ],
});

/** Registro por tipo e versão. Versões antigas permanecem enquanto houver instância que as use (ARQUITETURA §4). */
export const JOURNEY_DEFINITIONS: Readonly<Partial<Record<JourneyType, Readonly<Record<number, JourneyDefinition>>>>> = /*#__PURE__*/ deepFreeze({
  PORTFOLIO_POSICIONAMENTO: { 1: J4_PORTFOLIO_V1 },
  CONCORRENTES: { 1: J2_CONCORRENTES_V1 },
});
export const getJourneyDefinition = (type: JourneyType, version: number): JourneyDefinition | null => JOURNEY_DEFINITIONS[type]?.[version] ?? null;

// ── Validação da definição ───────────────────────────────────────────────────────────────────────────────────────
export const DEFINITION_ERRORS = [
  'TIPO_INVALIDO', 'VERSAO_INVALIDA', 'TEXTO_INVALIDO', 'SEM_ETAPAS', 'ID_INVALIDO', 'ID_DUPLICADO', 'DEPENDENCIA_INEXISTENTE', 'DEPENDENCIA_PROPRIA',
  'DEPENDENCIA_DUPLICADA', 'CICLO', 'LEITOR_DESCONHECIDO', 'CAPACIDADE_INVALIDA', 'REUSO_INVALIDO', 'ROTA_INVALIDA', 'ROTA_INEXISTENTE', 'ACAO_INVALIDA',
  'DISPENSA_INCONSISTENTE', 'ENTRADA_INVALIDA', 'CONCLUSAO_IMPOSSIVEL',
] as const;
export type DefinitionErrorCode = (typeof DEFINITION_ERRORS)[number];
export interface DefinitionError { code: DefinitionErrorCode; stepId?: string; detail: string }

const PATH_RE = /^\/[a-z0-9-]+(\/(:id|[a-z0-9-]+))*$/;
const segs = (p: string) => p.split('/').filter(Boolean);
/** Um caminho da definição casa com um padrão de rota da tela: mesmo número de segmentos; `:param` casa com qualquer segmento. */
export function matchesRoutePattern(path: string, pattern: string): boolean {
  const a = segs(path), b = segs(pattern);
  return a.length === b.length && a.every((s, i) => s === b[i] || s.startsWith(':') || b[i]!.startsWith(':'));
}
const routesOf = (r: JourneyRoute): JourneyRoute[] => [r, ...(r.fallback ? routesOf(r.fallback) : [])];

/**
 * Valida identificadores, versão, etapas, dependências (inexistente, própria, duplicada, ciclo), leitores, capacidades,
 * rotas e ações, consistência de dispensa e a existência de um caminho de conclusão. `knownRoutes` (padrões de
 * `apps/web/src/main.tsx`) é opcional: quando informado, toda rota precisa existir. Não lança; devolve a lista de erros.
 */
export function validateDefinition(def: JourneyDefinition, opts: { knownRoutes?: readonly string[] } = {}): DefinitionError[] {
  const errors: DefinitionError[] = [];
  const err = (code: DefinitionErrorCode, detail: string, stepId?: string) => errors.push(stepId ? { code, stepId, detail } : { code, detail });
  if (!(JOURNEY_TYPES as readonly string[]).includes(def.type)) err('TIPO_INVALIDO', `tipo desconhecido: ${String(def.type)}`);
  if (!Number.isInteger(def.version) || def.version < 1) err('VERSAO_INVALIDA', 'versão deve ser inteiro ≥ 1');
  if (!def.title?.trim() || !def.objective?.trim()) err('TEXTO_INVALIDO', 'título e objetivo obrigatórios');
  for (const s of Array.isArray(def.steps) ? def.steps : []) if (!['OBRIGATORIA', 'OPCIONAL', 'CONDICIONAL'].includes(s.kind)) err('TEXTO_INVALIDO', `tipo de etapa desconhecido: ${String(s.kind)}`, s.id);
  const steps = Array.isArray(def.steps) ? def.steps : [];
  if (!steps.length) { err('SEM_ETAPAS', 'definição sem etapas'); return errors; }
  const n = JOURNEY_NUMBER[def.type];
  const byId = new Map<string, JourneyStepDefinition>();
  for (const s of steps) {
    if (!isStepId(s.id) || (n && !s.id.startsWith(`J${n}-`))) err('ID_INVALIDO', `identificador fora do padrão J${n ?? 'n'}-mm`, String(s.id));
    if (byId.has(s.id)) err('ID_DUPLICADO', 'identificador repetido', s.id);
    byId.set(s.id, s);
  }
  for (const s of steps) {
    if (!s.title?.trim()) err('TEXTO_INVALIDO', 'etapa sem título', s.id);
    const seen = new Set<string>();
    for (const d of s.dependsOn) {
      if (d === s.id) err('DEPENDENCIA_PROPRIA', 'etapa depende de si mesma', s.id);
      else if (!byId.has(d)) err('DEPENDENCIA_INEXISTENTE', `dependência ${d} não existe na definição`, s.id);
      if (seen.has(d)) err('DEPENDENCIA_DUPLICADA', `dependência ${d} repetida`, s.id);
      seen.add(d);
    }
    if (s.reader !== null && !(JOURNEY_READERS as readonly string[]).includes(s.reader)) err('LEITOR_DESCONHECIDO', `leitor ${s.reader} não registrado`, s.id);
    for (const c of s.capabilities) if (!/^IM-CAP-\d{3}$/.test(c)) err('CAPACIDADE_INVALIDA', `capacidade ${c} fora do padrão IM-CAP-nnn`, s.id);
    for (const e of s.shared) if (!/^EC-\d{2}$/.test(e)) err('REUSO_INVALIDO', `etapa compartilhável ${e} fora do padrão EC-nn`, s.id);
    if (s.freshness !== null && s.freshness !== 'MODULO' && !(s.freshness in JOURNEY_FRESHNESS_DAYS)) err('TEXTO_INVALIDO', `atualidade ${String(s.freshness)} desconhecida`, s.id);
    if (s.action) {
      if (!s.action.label?.trim()) err('ACAO_INVALIDA', 'ação sem rótulo', s.id);
      for (const r of routesOf(s.action.route)) {
        if (!PATH_RE.test(r.path)) err('ROTA_INVALIDA', `rota ${r.path} fora do padrão`, s.id);
        else if (opts.knownRoutes && !opts.knownRoutes.some((p) => matchesRoutePattern(r.path, p))) err('ROTA_INEXISTENTE', `rota ${r.path} não existe na interface`, s.id);
        if (r.path.includes(':id') && !r.fallback && r === s.action.route) err('ROTA_INVALIDA', `rota ${r.path} com :id exige alternativa sem artefato`, s.id);
      }
    }
    // Lacuna: capacidade inexistente — sem leitor, sem ação, opcional e dispensada por padrão. O resto precisa de ação e leitor.
    if (s.completion === 'LACUNA') {
      if (s.kind !== 'OPCIONAL' || !s.defaultSkip || s.action || s.reader) err('DISPENSA_INCONSISTENTE', 'lacuna deve ser opcional, dispensada por padrão, sem leitor e sem ação', s.id);
    } else if (!s.action || !s.reader) err('ACAO_INVALIDA', 'etapa executável exige leitor e ação com rota', s.id);
    if (s.defaultSkip && (!s.skippable || s.kind !== 'OPCIONAL' || !s.defaultSkip.reason?.trim())) err('DISPENSA_INCONSISTENTE', 'dispensa por padrão exige etapa opcional, dispensável e com motivo', s.id);
  }
  // Ciclos (DFS com cores); só sobre dependências existentes.
  const color = new Map<string, 0 | 1 | 2>();
  const visit = (id: string, path: string[]): void => {
    color.set(id, 1);
    for (const d of byId.get(id)?.dependsOn ?? []) {
      if (!byId.has(d) || d === id) continue;
      if (color.get(d) === 1) err('CICLO', `ciclo: ${[...path.slice(path.indexOf(d)), d].join(' → ')}`, id);
      else if (!color.get(d)) visit(d, [...path, d]);
    }
    color.set(id, 2);
  };
  for (const id of byId.keys()) if (!color.get(id)) visit(id, [id]);
  const entry = def.entry ? byId.get(def.entry.stepId) : undefined;
  if (!entry || entry.phase !== 'CONDUCAO' || entry.completion === 'LACUNA') err('ENTRADA_INVALIDA', 'condição de entrada deve apontar para uma etapa de condução executável');
  // Caminho de conclusão: há obrigatória de condução; nenhuma obrigatória é lacuna; nenhuma obrigatória depende de etapa
  // que não pode ser concluída nem dispensada.
  const required = steps.filter((s) => s.kind !== 'OPCIONAL' && s.phase === 'CONDUCAO');
  if (!required.length) err('CONCLUSAO_IMPOSSIVEL', 'nenhuma etapa obrigatória de condução: a jornada não tem o que concluir');
  const reachable = (s: JourneyStepDefinition, seen = new Set<string>()): boolean => {
    if (seen.has(s.id)) return false;
    seen.add(s.id);
    const self = s.completion !== 'LACUNA' || s.skippable;
    return self && s.dependsOn.every((d) => { const x = byId.get(d); return !!x && x.phase === 'CONDUCAO' && reachable(x, new Set(seen)); });
  };
  for (const s of required) if (!reachable(s)) err('CONCLUSAO_IMPOSSIVEL', 'etapa obrigatória não pode ser concluída (dependência impossível ou de acompanhamento)', s.id);
  return errors;
}

// ── Exceções humanas por etapa (`journeys.step_overrides`, JRN-005) ──────────────────────────────────────────────
const IsoDate = z.string().refine((s) => !Number.isNaN(Date.parse(s)), 'data inválida');
const Reason = z.string().trim().min(3).max(1000);
export const StepOverrideSchema = z.object({
  skipped: z.object({ reason: Reason, by: z.string().uuid().nullable(), at: IsoDate, auto: z.boolean().optional() }).strict().optional(),
  confirmed: z.object({ by: z.string().uuid(), at: IsoDate, note: z.string().max(1000).optional() }).strict().optional(),
  reopened: z.object({ by: z.string().uuid(), at: IsoDate, reason: Reason }).strict().optional(),
  owner: z.object({ userId: z.string().uuid(), dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional() }).strict().optional(),
}).strict();
export type StepOverride = z.infer<typeof StepOverrideSchema>;
export type StepOverrides = Readonly<Partial<Record<StepId, StepOverride>>>;

/** Valida o jsonb de exceções contra a definição: só etapas existentes; dispensa só onde a definição permite. */
export function parseStepOverrides(def: JourneyDefinition, raw: unknown): { ok: true; value: StepOverrides } | { ok: false; errors: Array<{ stepId: string | null; detail: string }> } {
  const errors: Array<{ stepId: string | null; detail: string }> = [];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { ok: false, errors: [{ stepId: null, detail: 'exceções devem ser um objeto' }] };
  const value: Partial<Record<StepId, StepOverride>> = {};
  for (const [k, v] of Object.entries(raw)) {
    const step = def.steps.find((s) => s.id === k);
    if (!step) { errors.push({ stepId: k, detail: 'etapa inexistente na definição' }); continue; }
    const p = StepOverrideSchema.safeParse(v);
    if (!p.success) { errors.push({ stepId: k, detail: p.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ') }); continue; }
    if (p.data.skipped && !step.skippable) { errors.push({ stepId: k, detail: 'etapa não pode ser dispensada' }); continue; }
    value[step.id] = p.data;
  }
  return errors.length ? { ok: false, errors } : { ok: true, value };
}

// ── Leituras (o que um leitor devolve — CONTRATOS_ORQUESTRACAO §1.1) ─────────────────────────────────────────────
export interface ArtifactRef {
  entityType: string; entityId: string; versionNo: number | null; label: string; at: string | null;
  status: 'ATUAL' | 'DESATUALIZADO' | 'PARCIAL' | 'ARQUIVADO' | 'INDISPONIVEL';
}
export interface RunRef { entityType: string; entityId: string; status: 'QUEUED' | 'RUNNING' | 'FAILED'; startedAt: string | null }
export interface MissingItem { key: string; label: string; indispensable: boolean; route: string | null }
/** Fatos lidos do módulo dono, sem interpretação de jornada. */
export interface StepReading {
  available: boolean;
  artifact: ArtifactRef | null;
  run: RunRef | null;
  pendingReview: number;
  openDecisions: ReadonlyArray<{ id: string; state: string; band: string | null }>;
  missing: readonly MissingItem[];
  freshness: { at: string | null; limitDays: number | null; source: 'MODULO' | 'CAMADA' | null };
  /** Regra 4 (escopo): `null` quando o artefato não grava o parâmetro. */
  compatible: boolean | null;
  /** Etapa condicional: motivo pelo qual a condição NÃO se aplica agora (ex.: nenhuma rede social com conexão autorizada). */
  notApplicable?: string | null;
}

const DAY_MS = 86_400_000;
const time = (s: string | null | undefined): number | null => { if (!s) return null; const t = Date.parse(s); return Number.isNaN(t) ? null : t; };

/** Classifica a atualidade (C4). Só classifica: não atualiza, não coleta, não chama nada. `asOf` é a data de referência. */
export function deriveFreshness(reading: StepReading | null, step: Pick<JourneyStepDefinition, 'freshness'>, asOf: string): Freshness {
  const at = reading?.freshness.at ?? reading?.artifact?.at ?? null;
  const na: Freshness = { status: 'NAO_APLICAVEL', at, limitDays: null, limitSource: null, ageDays: null };
  if (!reading?.artifact || step.freshness === null || reading.artifact.status === 'ARQUIVADO' || reading.artifact.status === 'INDISPONIVEL') return na;
  const moduleLimit = reading.freshness.source === 'MODULO' ? reading.freshness.limitDays : null;
  const layerLimit = step.freshness !== 'MODULO' ? JOURNEY_FRESHNESS_DAYS[step.freshness] : null;
  const limitDays = moduleLimit ?? layerLimit;
  const limitSource = moduleLimit !== null ? 'MODULO' : layerLimit !== null ? 'CAMADA' : null;
  const t = time(at), ref = time(asOf);
  const ageDays = t !== null && ref !== null ? Math.max(0, Math.floor((ref - t) / DAY_MS)) : null;
  // O próprio módulo marcou como desatualizado (ex.: contexto STALE, R09): prevalece.
  if (reading.artifact.status === 'DESATUALIZADO') return { status: 'DESATUALIZADO', at, limitDays, limitSource: limitSource ?? 'MODULO', ageDays };
  if (ageDays === null || limitDays === null) return { status: 'DESCONHECIDO', at, limitDays, limitSource, ageDays };
  return { status: ageDays > limitDays ? 'DESATUALIZADO' : 'ATUAL', at, limitDays, limitSource, ageDays };
}

// ── Derivação da etapa (MAQUINA §3.2) ────────────────────────────────────────────────────────────────────────────
export interface DerivedStep {
  stepId: StepId;
  state: StepState;
  reason: StepReason;
  /** `INDISPONIVEL` = leitor falhou ou não foi lido (não é estado da máquina; a etapa nunca conta como concluída). */
  reading: 'OK' | 'INDISPONIVEL' | 'SEM_LEITOR';
  freshness: Freshness;
  artifact: ArtifactRef | null;
  skipped: { reason: string; auto: boolean } | null;
  pendingReview: number;
  openDecisions: number;
  missing: readonly MissingItem[];
  owner: { userId: string; dueDate: string | null } | null;
  /** Execução do módulo em andamento ou falha recente (com o identificador real — IM-NFR-012); só leitura, nunca disparada. */
  run: RunRef | null;
}
export interface StepDerivationInput {
  /** `null` = leitor falhou; `undefined` = não lido. */
  reading: StepReading | null | undefined;
  override: StepOverride | undefined;
  /** Estados já derivados das dependências diretas (mesma ordem de `step.dependsOn`). */
  dependencies: readonly DerivedStep[];
  /** Vínculo ACEITO_COM_AVISO ativo para a etapa (regra 11: "usar mesmo assim"). */
  reuseAccepted: boolean;
  asOf: string;
}

const after = (a: string | null | undefined, b: string | null | undefined) => { const x = time(a), y = time(b); return x !== null && y !== null && x > y; };

/** Estado de UMA etapa: primeira regra verdadeira decide (MAQUINA §3.2). Pura: não grava, não consulta, não altera a entrada. */
export function deriveStepState(step: JourneyStepDefinition, input: StepDerivationInput): DerivedStep {
  const { override, asOf } = input;
  const reopenedAt = override?.reopened?.at ?? null;
  const reading = step.reader === null ? null : input.reading ?? null;
  const readingState: DerivedStep['reading'] = step.reader === null ? 'SEM_LEITOR' : reading ? 'OK' : 'INDISPONIVEL';
  // Reabertura desconsidera artefato anterior a ela (regras 9–11) e confirmação anterior.
  const rawArtifact = reading?.artifact && reading.artifact.status !== 'ARQUIVADO' && reading.artifact.status !== 'INDISPONIVEL' ? reading.artifact : null;
  const artifact = rawArtifact && (!reopenedAt || after(rawArtifact.at, reopenedAt)) ? rawArtifact : null;
  const confirmed = override?.confirmed && (!reopenedAt || after(override.confirmed.at, reopenedAt)) ? override.confirmed : null;
  const freshness = deriveFreshness(reading && artifact ? reading : null, step, asOf);
  const base = {
    stepId: step.id, reading: readingState, freshness, artifact: artifact ? { ...artifact } : null, pendingReview: reading?.pendingReview ?? 0,
    openDecisions: reading?.openDecisions.length ?? 0, missing: (reading?.missing ?? []).map((m) => ({ ...m })), owner: override?.owner ? { userId: override.owner.userId, dueDate: override.owner.dueDate ?? null } : null,
    run: reading?.run ? { ...reading.run } : null,
  };
  const out = (state: StepState, reason: StepReason, skipped: DerivedStep['skipped'] = null): DerivedStep => ({ ...base, state, reason, skipped });

  // 1. Dispensa: exceção humana (se posterior à reabertura) ou padrão da definição (se nunca reaberta). Só onde a definição permite.
  if (step.skippable) {
    const s = override?.skipped;
    if (s && (!reopenedAt || !after(reopenedAt, s.at))) return out('DISPENSADA', s.auto ? 'DISPENSADA_POR_PADRAO' : 'DISPENSADA_PELO_USUARIO', { reason: s.reason, auto: !!s.auto });
    if (!s && step.defaultSkip && !reopenedAt) return out('DISPENSADA', 'DISPENSADA_POR_PADRAO', { reason: step.defaultSkip.reason, auto: true });
  }
  // 1b. Condicional cuja condição não se aplica agora (lido no módulo): dispensada automaticamente, com o motivo honesto.
  if (step.kind === 'CONDICIONAL' && reading?.notApplicable) return out('DISPENSADA', 'CONDICAO_NAO_APLICAVEL', { reason: reading.notApplicable, auto: true });
  // 2–3. Dependências.
  if (input.dependencies.some((d) => d.state === 'BLOQUEADA')) return out('BLOQUEADA', 'DEPENDENCIA_BLOQUEADA');
  if (input.dependencies.some((d) => !SATISFIES_DEPENDENCY.has(d.state))) return out('NAO_INICIADA', 'DEPENDENCIA_PENDENTE');
  if (step.completion === 'LACUNA') return out('BLOQUEADA', 'CAPACIDADE_INDISPONIVEL');
  if (!reading) return out('BLOQUEADA', 'LEITURA_INDISPONIVEL');
  // 4–5. Execução no módulo.
  if (reading.run && (reading.run.status === 'QUEUED' || reading.run.status === 'RUNNING')) return out('EM_ANDAMENTO', 'EXECUCAO_EM_ANDAMENTO');
  if (reading.run?.status === 'FAILED' && !(artifact && (!reading.run.startedAt || !after(reading.run.startedAt, artifact.at)))) return out('BLOQUEADA', 'EXECUCAO_FALHOU');
  // 6. Dado indispensável ausente (o leitor já descarta os que têm alternativa aceita).
  if (reading.missing.some((m) => m.indispensable)) return out('AGUARDANDO_DADO', 'DADO_AUSENTE');
  // 7–8. Revisão e decisão sobre resultado existente.
  if (artifact && reading.pendingReview > 0) return out('AGUARDANDO_REVISAO', 'REVISAO_PENDENTE');
  if (artifact && step.requiresDecision && reading.openDecisions.length > 0) return out('AGUARDANDO_DECISAO', 'DECISAO_PENDENTE');
  // 9–10. Conclusão (por artefato atual e compatível, ou por confirmação registrada) e desatualização.
  const byArtifact = step.completion !== 'CONFIRMACAO' && artifact && reading.compatible !== false;
  const byConfirmation = (step.completion === 'CONFIRMACAO' || step.completion === 'ARTEFATO_OU_CONFIRMACAO') && confirmed;
  if (byArtifact || byConfirmation) {
    const doneAt = byArtifact ? artifact!.at : confirmed!.at;
    if (byArtifact && freshness.status === 'DESATUALIZADO') return out('DESATUALIZADA', 'RESULTADO_DESATUALIZADO');
    if (input.dependencies.some((d) => d.state !== 'DISPENSADA' && after(d.artifact?.at, doneAt))) return out('DESATUALIZADA', 'MUDANCA_A_MONTANTE');
    if (!byArtifact) return out('CONCLUIDA', 'CONFIRMADA_MANUALMENTE');
    return artifact!.status === 'PARCIAL' ? out('CONCLUIDA_COM_LIMITACOES', 'RESULTADO_PARCIAL') : out('CONCLUIDA', 'RESULTADO_ATUAL');
  }
  // 11. Resultado incompatível sem aceite: aceite registrado ("usar mesmo assim") conclui com o aviso.
  if (artifact && reading.compatible === false) {
    if (input.reuseAccepted) return artifact.status === 'PARCIAL' ? out('CONCLUIDA_COM_LIMITACOES', 'RESULTADO_PARCIAL') : out('CONCLUIDA', 'RESULTADO_ATUAL');
    return out('PRONTA_PARA_COMECAR', 'RESULTADO_INCOMPATIVEL');
  }
  // 12. Dependências satisfeitas (e capacidade disponível — C1).
  if (!reading.available) return out('BLOQUEADA', 'CAPACIDADE_INDISPONIVEL');
  return out('PRONTA_PARA_COMECAR', step.completion === 'CONFIRMACAO' && artifact ? 'AGUARDANDO_CONFIRMACAO' : 'PRONTA');
}

/** Tudo o que a derivação de uma instância precisa (montado pelo serviço na JRN-006). */
export interface JourneySnapshot {
  readings: Readonly<Partial<Record<StepId, StepReading | null>>>;
  overrides: StepOverrides;
  /** Etapas com vínculo `ACEITO_COM_AVISO` ativo (`journey_links`). */
  reuseAccepted?: readonly StepId[];
  /** Data de referência explícita (ISO). */
  asOf: string;
}

/** Deriva todas as etapas na ordem das dependências. Definição inválida (ciclo, dependência inexistente) → lança: valide antes. */
export function deriveSteps(def: JourneyDefinition, snap: JourneySnapshot): DerivedStep[] {
  const done = new Map<StepId, DerivedStep>();
  const byId = new Map(def.steps.map((s) => [s.id, s] as const));
  const go = (id: StepId, stack: ReadonlySet<StepId>): DerivedStep => {
    const hit = done.get(id);
    if (hit) return hit;
    const step = byId.get(id);
    if (!step || stack.has(id)) throw new Error(`definição inválida em ${id}: valide com validateDefinition`);
    const next = new Set(stack).add(id);
    const d = deriveStepState(step, {
      reading: snap.readings[id], override: snap.overrides[id], dependencies: step.dependsOn.map((x) => go(x, next)),
      reuseAccepted: !!snap.reuseAccepted?.includes(id), asOf: snap.asOf,
    });
    done.set(id, d);
    return d;
  };
  return def.steps.map((s) => go(s.id, new Set()));
}

// ── Entrada, progresso, conclusão, pendências e próxima ação ────────────────────────────────────────────────────
const find = (steps: readonly DerivedStep[], id: StepId) => steps.find((s) => s.stepId === id);
const conduction = (def: JourneyDefinition) => def.steps.filter((s) => s.phase === 'CONDUCAO');

/** Condição de entrada (catálogo e criação): etapa de entrada lida e sem dado indispensável ausente nem bloqueio. */
export function entryMet(def: JourneyDefinition, steps: readonly DerivedStep[]): { ok: boolean; missing: readonly MissingItem[] } {
  const s = find(steps, def.entry.stepId);
  if (!s || s.reading !== 'OK') return { ok: false, missing: [] };
  return { ok: !['AGUARDANDO_DADO', 'BLOQUEADA', 'NAO_INICIADA'].includes(s.state), missing: s.missing.filter((m) => m.indispensable) };
}

export interface JourneyProgress {
  /** Numerador: obrigatórias de condução em CONCLUIDA, CONCLUIDA_COM_LIMITACOES ou DESATUALIZADA (esta com aviso). */
  requiredDone: number;
  /** Denominador: obrigatórias (e condicionais que se aplicam) de condução, menos as DISPENSADAS. Acompanhamento não conta. */
  requiredApplicable: number;
  optionalDone: number;
  /** Opcionais de condução não dispensadas (exibidas à parte; nunca entram no percentual). */
  optionalTotal: number;
  skipped: number;
  stale: number;
  /** Etapas cuja leitura falhou: impedem o percentual (nunca porcentagem sobre dado ausente). */
  unknown: StepId[];
  /** ⌊100 × requiredDone ÷ requiredApplicable⌋ — `null` sem denominador ou com leitura indisponível. Nunca 100 com pendência. */
  percent: number | null;
}
/** Progresso determinístico. Etapa da definição sem derivação conta como desconhecida. */
export function progressOf(def: JourneyDefinition, steps: readonly DerivedStep[]): JourneyProgress {
  const p: JourneyProgress = { requiredDone: 0, requiredApplicable: 0, optionalDone: 0, optionalTotal: 0, skipped: 0, stale: 0, unknown: [], percent: null };
  for (const s of conduction(def)) {
    const d = find(steps, s.id);
    if (!d || d.reading === 'INDISPONIVEL') p.unknown.push(s.id);
    if (d?.state === 'DISPENSADA') { p.skipped++; continue; }
    const isDone = !!d && STEP_DONE_STATES.has(d.state);
    if (d?.state === 'DESATUALIZADA') p.stale++;
    if (s.kind !== 'OPCIONAL') { p.requiredApplicable++; if (isDone) p.requiredDone++; } else { p.optionalTotal++; if (isDone) p.optionalDone++; }
  }
  p.percent = p.requiredApplicable > 0 && !p.unknown.length ? Math.floor((100 * p.requiredDone) / p.requiredApplicable) : null;
  return p;
}

export interface CompletionCheck { ok: boolean; pending: StepId[]; reasons: Array<{ stepId: StepId; state: StepState | null; reason: StepReason | 'NAO_DERIVADA' }> }
/**
 * Obrigatórias (e condicionais) de condução CONCLUIDA/CONCLUIDA_COM_LIMITACOES, ou DISPENSADA onde a definição permite
 * (MAQUINA §4) ou porque a condição não se aplica (condicional).
 */
export function canComplete(def: JourneyDefinition, steps: readonly DerivedStep[]): CompletionCheck {
  const reasons: CompletionCheck['reasons'] = [];
  const required = conduction(def).filter((s) => s.kind !== 'OPCIONAL');
  for (const s of required) {
    const d = find(steps, s.id);
    if (!d) { reasons.push({ stepId: s.id, state: null, reason: 'NAO_DERIVADA' }); continue; }
    const skippedOk = d.state === 'DISPENSADA' && (s.skippable || d.reason === 'CONDICAO_NAO_APLICAVEL');
    if (d.reading === 'INDISPONIVEL' || !(COMPLETES_JOURNEY.has(d.state) || skippedOk)) reasons.push({ stepId: s.id, state: d.state, reason: d.reason });
  }
  return { ok: required.length > 0 && reasons.length === 0, pending: reasons.map((r) => r.stepId), reasons };
}

export interface PendingItem { stepId: StepId; kind: 'DECISAO' | 'REVISAO'; count: number }
/** Pendências humanas (C9): decisões antes de revisões, depois a ordem da definição. */
export function pendingOf(def: JourneyDefinition, steps: readonly DerivedStep[]): PendingItem[] {
  const items: PendingItem[] = [];
  for (const kind of ['DECISAO', 'REVISAO'] as const) {
    for (const s of def.steps) {
      const d = find(steps, s.id);
      if (!d || d.state === 'DISPENSADA') continue;
      const count = kind === 'DECISAO' ? (s.requiresDecision ? d.openDecisions : 0) : d.pendingReview;
      if (count > 0) items.push({ stepId: s.id, kind, count });
    }
  }
  return items;
}

export const NEXT_ACTION_KINDS = ['DECIDIR', 'REVISAR', 'INFORMAR_DADO', 'DESBLOQUEAR', 'ACOMPANHAR_EXECUCAO', 'EXECUTAR', 'CONFIRMAR', 'ATUALIZAR', 'CONCLUIR_JORNADA', 'ACOMPANHAR'] as const;
export type NextActionKind = (typeof NEXT_ACTION_KINDS)[number];
export interface NextAction {
  kind: NextActionKind;
  /** `null` só em CONCLUIR_JORNADA (a confirmação é feita na própria jornada). */
  stepId: StepId | null;
  title: string;
  why: string;
  reason: StepReason | 'TODAS_AS_OBRIGATORIAS_CONCLUIDAS';
  /** Deep link para a tela existente; `null` quando não há tela (concluir a jornada). */
  href: string | null;
  /** A etapa está bloqueada: a ação é desbloquear, não executar. */
  blocked: boolean;
  blockReason: StepReason | null;
  owner: string | null;
  dueDate: string | null;
}

/** Monta o deep link: `:id` vem do artefato vigente; sem artefato, usa a alternativa da rota. */
export function routeHref(route: JourneyRoute, artifactId: string | null): string | null {
  if (route.path.includes(':id') && !artifactId) return route.fallback ? routeHref(route.fallback, null) : null;
  const path = route.path.replace(':id', encodeURIComponent(artifactId ?? ''));
  const q = Object.entries(route.query ?? {}).map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
  return q ? `${path}?${q}` : path;
}

const KIND_BY_STATE: Partial<Record<StepState, NextActionKind>> = {
  AGUARDANDO_DECISAO: 'DECIDIR', AGUARDANDO_REVISAO: 'REVISAR', AGUARDANDO_DADO: 'INFORMAR_DADO', BLOQUEADA: 'DESBLOQUEAR',
  EM_ANDAMENTO: 'ACOMPANHAR_EXECUCAO', PRONTA_PARA_COMECAR: 'EXECUTAR', DESATUALIZADA: 'ATUALIZAR',
};
/** Prioridade: decisão > revisão > demais (na ordem da definição). Revisão e decisão destravam a jornada sem custo. */
const PRIORITY = (st: StepState) => (st === 'AGUARDANDO_DECISAO' ? 0 : st === 'AGUARDANDO_REVISAO' ? 1 : 2);

/**
 * Uma única próxima ação, estável para a mesma entrada (C10). `null` sem instância e em PAUSADA, CONCLUIDA, CANCELADA e
 * ARQUIVADA. Em `EM_ACOMPANHAMENTO`, aponta a etapa de acompanhamento. Em `EM_ANDAMENTO`: para cada obrigatória pendente,
 * na ordem, resolve a etapa que de fato pode agir (a própria ou a dependência que a segura); escolhe pela prioridade;
 * sem pendência, atualizar a primeira desatualizada; sem nada, concluir a jornada.
 */
export function nextAction(def: JourneyDefinition, steps: readonly DerivedStep[], lifecycle: JourneyLifecycle | null): NextAction | null {
  if (lifecycle !== 'EM_ANDAMENTO' && lifecycle !== 'EM_ACOMPANHAMENTO') return null;
  const byId = new Map(def.steps.map((s) => [s.id, s] as const));
  const make = (s: JourneyStepDefinition, d: DerivedStep, kind: NextActionKind): NextAction => {
    const missingRoute = kind === 'INFORMAR_DADO' ? d.missing.find((m) => m.indispensable && m.route)?.route ?? null : null;
    const blocked = d.state === 'BLOQUEADA';
    return {
      kind, stepId: s.id, title: s.title, why: STEP_REASON_LABEL[d.reason], reason: d.reason,
      href: missingRoute ?? (s.action ? routeHref(s.action.route, d.artifact?.entityId ?? null) : null),
      blocked, blockReason: blocked ? d.reason : null, owner: d.owner?.userId ?? null, dueDate: d.owner?.dueDate ?? null,
    };
  };
  if (lifecycle === 'EM_ACOMPANHAMENTO') {
    for (const s of def.steps.filter((x) => x.phase === 'ACOMPANHAMENTO')) {
      const d = find(steps, s.id);
      if (d && d.state !== 'DISPENSADA' && s.action) return make(s, d, 'ACOMPANHAR');
    }
    return null;
  }
  const actionable = (id: StepId, seen: Set<StepId>): DerivedStep | null => {
    const d = find(steps, id), s = byId.get(id);
    if (!d || !s || seen.has(id)) return null;
    seen.add(id);
    if (d.state === 'NAO_INICIADA' || (d.state === 'BLOQUEADA' && d.reason === 'DEPENDENCIA_BLOQUEADA')) {
      for (const dep of s.dependsOn) { const x = find(steps, dep); if (x && !SATISFIES_DEPENDENCY.has(x.state)) { const r = actionable(dep, seen); if (r) return r; } }
      return null;
    }
    return d;
  };
  const order = new Map(def.steps.map((s, i) => [s.id, i] as const));
  const candidates = new Map<StepId, DerivedStep>();
  for (const s of conduction(def).filter((x) => x.kind !== 'OPCIONAL')) {
    const d = find(steps, s.id);
    if (!d || STEP_DONE_STATES.has(d.state) || d.state === 'DISPENSADA') continue;
    const a = actionable(s.id, new Set());
    if (a) candidates.set(a.stepId, a);
  }
  const best = [...candidates.values()].sort((a, b) => PRIORITY(a.state) - PRIORITY(b.state) || order.get(a.stepId)! - order.get(b.stepId)!)[0];
  if (best) {
    const s = byId.get(best.stepId)!;
    const kind = best.reason === 'AGUARDANDO_CONFIRMACAO' ? 'CONFIRMAR' : KIND_BY_STATE[best.state] ?? 'EXECUTAR';
    return make(s, best, kind);
  }
  const stale = conduction(def).find((s) => s.kind !== 'OPCIONAL' && find(steps, s.id)?.state === 'DESATUALIZADA');
  if (stale) return make(stale, find(steps, stale.id)!, 'ATUALIZAR');
  if (!canComplete(def, steps).ok) return null;
  return { kind: 'CONCLUIR_JORNADA', stepId: null, title: def.title, why: 'Todas as etapas obrigatórias estão concluídas. Confirme a conclusão da jornada.',
    reason: 'TODAS_AS_OBRIGATORIAS_CONCLUIDAS', href: null, blocked: false, blockReason: null, owner: null, dueDate: null };
}

export interface JourneyStateView {
  state: JourneyState;
  /** `CATALOGO` (sem instância), `PERSISTIDO` (ciclo de vida prevalece) ou `DERIVADO` (refinamento de EM_ANDAMENTO). */
  source: 'CATALOGO' | 'PERSISTIDO' | 'DERIVADO';
  /** Etapa que definiu o estado derivado (a da próxima ação). */
  stepId: StepId | null;
  /** Indicadores ao lado do estado persistido (ex.: "concluída — 1 etapa desatualizada"). */
  staleSteps: StepId[];
}
/**
 * Estado exibido (MAQUINA §3.1): sem instância → NAO_INICIADA/PRONTA_PARA_COMECAR pela condição de entrada; ciclo de
 * vida diferente de EM_ANDAMENTO prevalece (estados terminais nunca são mascarados); EM_ANDAMENTO → BLOQUEADA >
 * AGUARDANDO_DECISAO > AGUARDANDO_REVISAO > AGUARDANDO_DADO > EM_ANDAMENTO, avaliado só sobre a etapa da próxima ação.
 */
export function deriveJourneyState(def: JourneyDefinition, lifecycle: JourneyLifecycle | null, steps: readonly DerivedStep[]): JourneyStateView {
  const staleSteps = steps.filter((s) => s.state === 'DESATUALIZADA').map((s) => s.stepId);
  if (lifecycle === null) return { state: entryMet(def, steps).ok ? 'PRONTA_PARA_COMECAR' : 'NAO_INICIADA', source: 'CATALOGO', stepId: def.entry.stepId, staleSteps };
  if (JOURNEY_SETTLED_LIFECYCLES.has(lifecycle)) return { state: lifecycle, source: 'PERSISTIDO', stepId: null, staleSteps };
  const a = nextAction(def, steps, lifecycle);
  const st = a?.stepId ? find(steps, a.stepId)?.state : undefined;
  const state: JourneyState = st && (JOURNEY_ACTIVE_STATES as readonly string[]).includes(st) ? (st as JourneyState) : 'EM_ANDAMENTO';
  return { state, source: 'DERIVADO', stepId: a?.stepId ?? null, staleSteps };
}

// ── Ciclo de vida persistido: transições, ações de etapa e vínculos (INTELIMOVE-JRN-005, MAQUINA §4–§7) ────────────
/** Ciclos de vida "vivos": entram no índice de unicidade por escopo (`journeys_live_scope_uq`). */
export const JOURNEY_LIVE_LIFECYCLES: ReadonlySet<JourneyLifecycle> = new Set(['EM_ANDAMENTO', 'PAUSADA', 'EM_ACOMPANHAMENTO']);
/**
 * Transições permitidas da instância (MAQUINA §4). De `ARQUIVADA` só se sai restaurando para `lifecycle_before_archive`
 * (tratado por `journeyTransitionOf`). Qualquer outra → 409 `invalid_transition`.
 */
export const JOURNEY_TRANSITIONS: Readonly<Record<JourneyLifecycle, readonly JourneyLifecycle[]>> = {
  EM_ANDAMENTO: ['PAUSADA', 'CONCLUIDA', 'CANCELADA'],
  PAUSADA: ['EM_ANDAMENTO', 'CANCELADA'],
  CONCLUIDA: ['EM_ACOMPANHAMENTO', 'EM_ANDAMENTO', 'ARQUIVADA'],
  EM_ACOMPANHAMENTO: ['EM_ANDAMENTO', 'ARQUIVADA'],
  CANCELADA: ['ARQUIVADA'],
  ARQUIVADA: [],
};
/** Eventos `journey.*` gravados em `audit_events` (MAQUINA §6). Criar = iniciar: um único `journey.started`. */
export const JOURNEY_EVENTS = [
  'journey.started', 'journey.paused', 'journey.resumed', 'journey.completed', 'journey.tracking.started', 'journey.reopened', 'journey.cancelled',
  'journey.archived', 'journey.restored', 'journey.step.skipped', 'journey.step.unskipped', 'journey.step.confirmed', 'journey.step.reopened',
  'journey.step.assigned', 'journey.link.added', 'journey.link.removed', 'journey.reuse.accepted',
] as const;
export type JourneyEvent = (typeof JOURNEY_EVENTS)[number];
export const JOURNEY_EVENT_LABEL: Record<JourneyEvent, string> = {
  'journey.started': 'Jornada iniciada', 'journey.paused': 'Jornada pausada', 'journey.resumed': 'Jornada retomada', 'journey.completed': 'Jornada concluída',
  'journey.tracking.started': 'Acompanhamento iniciado', 'journey.reopened': 'Jornada reaberta', 'journey.cancelled': 'Jornada cancelada', 'journey.archived': 'Jornada arquivada',
  'journey.restored': 'Jornada restaurada', 'journey.step.skipped': 'Etapa dispensada', 'journey.step.unskipped': 'Dispensa desfeita', 'journey.step.confirmed': 'Etapa confirmada',
  'journey.step.reopened': 'Etapa reaberta', 'journey.step.assigned': 'Responsável definido', 'journey.link.added': 'Resultado vinculado', 'journey.link.removed': 'Vínculo removido',
  'journey.reuse.accepted': 'Resultado aceito com aviso',
};
export interface JourneyTransitionRule { allowed: boolean; event: JourneyEvent | null; justification: boolean }
/**
 * Regra de UMA transição (pura). `to` igual ao atual não é transição (o serviço responde `changed: false`).
 * Restaurar: de `ARQUIVADA` para o `lifecycle_before_archive` gravado. Justificativa: cancelar e reabrir.
 */
export function journeyTransitionOf(from: JourneyLifecycle, to: JourneyLifecycle, beforeArchive: JourneyLifecycle | null): JourneyTransitionRule {
  const no: JourneyTransitionRule = { allowed: false, event: null, justification: false };
  if (from === 'ARQUIVADA') return beforeArchive && to === beforeArchive && to !== 'ARQUIVADA' ? { allowed: true, event: 'journey.restored', justification: false } : no;
  if (!JOURNEY_TRANSITIONS[from].includes(to)) return no;
  if (to === 'PAUSADA') return { allowed: true, event: 'journey.paused', justification: false };
  if (to === 'CONCLUIDA') return { allowed: true, event: 'journey.completed', justification: false };
  if (to === 'EM_ACOMPANHAMENTO') return { allowed: true, event: 'journey.tracking.started', justification: false };
  if (to === 'CANCELADA') return { allowed: true, event: 'journey.cancelled', justification: true };
  if (to === 'ARQUIVADA') return { allowed: true, event: 'journey.archived', justification: false };
  return from === 'PAUSADA' ? { allowed: true, event: 'journey.resumed', justification: false } : { allowed: true, event: 'journey.reopened', justification: true };
}

/** Ações humanas sobre uma etapa (MAQUINA §5), gravadas em `step_overrides`. */
export const JOURNEY_STEP_ACTIONS = ['DISPENSAR', 'DESFAZER_DISPENSA', 'CONFIRMAR', 'REABRIR', 'ATRIBUIR'] as const;
export type JourneyStepActionKind = (typeof JOURNEY_STEP_ACTIONS)[number];
export const JOURNEY_STEP_ACTION_EVENT: Record<JourneyStepActionKind, JourneyEvent> = {
  DISPENSAR: 'journey.step.skipped', DESFAZER_DISPENSA: 'journey.step.unskipped', CONFIRMAR: 'journey.step.confirmed', REABRIR: 'journey.step.reopened', ATRIBUIR: 'journey.step.assigned',
};

/** Relação do vínculo (MODELO_LOGICO §2.2). */
export const JOURNEY_LINK_RELATIONS = ['RESULTADO', 'EXECUCAO', 'DECISAO', 'INSUMO'] as const;
export type JourneyLinkRelation = (typeof JOURNEY_LINK_RELATIONS)[number];
/** Reaproveitamento: AUTOMATICO só pelo serviço (conclusão); pessoas vinculam como MANUAL ou ACEITO_COM_AVISO. */
export const JOURNEY_LINK_REUSE = ['AUTOMATICO', 'ACEITO_COM_AVISO', 'MANUAL'] as const;
export type JourneyLinkReuse = (typeof JOURNEY_LINK_REUSE)[number];
/** Allowlist global de `journey_links.entity_type` (MODELO_LOGICO §2.2): referência suave tipo + id, sem FK. */
export const JOURNEY_LINK_ENTITY_TYPES = [
  'company_context_version', 'company_offering', 'portfolio_analysis', 'price_series', 'ecosystem_relation', 'battlecard', 'company_competitor', 'market_analysis',
  'segmentation_analysis', 'market_sizing_analysis', 'environment_analysis', 'scenario_set', 'strategic_record', 'executive_report', 'target_company', 'person', 'source',
  'signal', 'commercial_map', 'commercial_result', 'learning_report', 'crm_delivery', 'competitor_research_run', 'target_research_run', 'target_suggestion_run', 'market_run', 'people_run',
] as const;
export type JourneyLinkEntityType = (typeof JOURNEY_LINK_ENTITY_TYPES)[number];
/** Tipos que cada jornada aceita vincular (subconjunto da allowlist; os demais → 422 `entity_incompatible`). */
export const JOURNEY_LINKABLE_ENTITIES: Readonly<Partial<Record<JourneyType, readonly JourneyLinkEntityType[]>>> = {
  PORTFOLIO_POSICIONAMENTO: ['company_offering', 'portfolio_analysis', 'price_series', 'battlecard', 'strategic_record', 'scenario_set'],
  CONCORRENTES: ['company_competitor', 'competitor_research_run', 'battlecard', 'market_analysis', 'market_run', 'source', 'signal', 'strategic_record'],
};

// ── Escopo da instância (idempotência por escopo vivo — IM-JRN-121) ──────────────────────────────────────────────
/** J4: organização inteira (`{}`) ou um recorte de ofertas confirmadas (`{ offeringIds }`). */
export const J4ScopeSchema = z.object({ offeringIds: z.array(z.string().uuid()).min(1).max(50).optional() }).strict();
/**
 * J2: todos os concorrentes da organização (`{}`) ou um recorte (`{ competitorIds }`). Limite de 20 por instância (risco da
 * JRN-010: custo de leitura com muitos concorrentes).
 */
export const J2ScopeSchema = z.object({ competitorIds: z.array(z.string().uuid()).min(1).max(20).optional() }).strict();
export const JOURNEY_SCOPE_SCHEMAS: Readonly<Partial<Record<JourneyType, z.ZodType<Record<string, unknown>>>>> = { PORTFOLIO_POSICIONAMENTO: J4ScopeSchema, CONCORRENTES: J2ScopeSchema };
export type JourneyScope = { offeringIds?: string[]; competitorIds?: string[] };
const canonicalIds = (ids: readonly string[] | undefined) => [...new Set((ids ?? []).map((x) => x.toLowerCase()))].sort();
/** Forma canônica do escopo (ids distintos, minúsculos e ordenados): base de `journeys.scope_key`. */
export function journeyScopeKey(scope: JourneyScope): string {
  const offerings = canonicalIds(scope.offeringIds);
  if (offerings.length) return `OFERTAS:${offerings.join(',')}`;
  const competitors = canonicalIds(scope.competitorIds);
  return competitors.length ? `CONCORRENTES:${competitors.join(',')}` : 'ORGANIZACAO';
}
/** Valida o escopo pela definição do tipo e devolve a forma canônica (ids sem repetição). */
export function parseJourneyScope(type: JourneyType, raw: unknown): { ok: true; scope: JourneyScope; key: string } | { ok: false; detail: string } {
  const schema = JOURNEY_SCOPE_SCHEMAS[type];
  if (!schema) return { ok: false, detail: 'tipo de jornada indisponível' };
  const p = schema.safeParse(raw ?? {});
  if (!p.success) return { ok: false, detail: p.error.issues.map((i) => `${i.path.join('.') || 'escopo'}: ${i.message}`).join('; ') };
  const s = p.data as JourneyScope;
  const scope: JourneyScope = s.offeringIds ? { offeringIds: canonicalIds(s.offeringIds) } : s.competitorIds ? { competitorIds: canonicalIds(s.competitorIds) } : {};
  return { ok: true, scope, key: journeyScopeKey(scope) };
}

// ── Requisições (zod `.strict()`: recusam `tenantId`/`orgId` e propriedades extras) ─────────────────────────────
const Justification = z.string().trim().min(3).max(1000);
const Version = z.number().int().min(1);
export const CreateJourneyRequest = z.object({
  type: z.enum(JOURNEY_TYPES),
  scope: z.record(z.string(), z.unknown()).optional(),
  title: z.string().trim().min(3).max(160).optional(),
  ownerUserId: z.string().uuid().optional(),
}).strict();
export type CreateJourneyRequest = z.infer<typeof CreateJourneyRequest>;
export const JourneyTransitionRequest = z.object({ version: Version, to: z.enum(JOURNEY_LIFECYCLES), justification: Justification.optional() }).strict();
export type JourneyTransitionRequest = z.infer<typeof JourneyTransitionRequest>;
export const JourneyStepActionRequest = z.object({
  version: Version,
  action: z.enum(JOURNEY_STEP_ACTIONS),
  reason: Justification.optional(),
  note: z.string().trim().max(1000).optional(),
  ownerUserId: z.string().uuid().optional(),
  dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
}).strict();
export type JourneyStepActionRequest = z.infer<typeof JourneyStepActionRequest>;
export const JourneyLinkRequest = z.object({
  version: Version,
  stepId: z.string().regex(STEP_ID_RE).optional(),
  relation: z.enum(JOURNEY_LINK_RELATIONS),
  entityType: z.string().regex(/^[a-z][a-z_]{1,59}$/),
  entityId: z.string().uuid(),
  reuse: z.enum(['MANUAL', 'ACEITO_COM_AVISO']),
  justification: Justification.optional(),
}).strict();
export type JourneyLinkRequest = z.infer<typeof JourneyLinkRequest>;
export const JourneyLinkRemoveRequest = z.object({ version: Version, justification: Justification }).strict();
export type JourneyLinkRemoveRequest = z.infer<typeof JourneyLinkRemoveRequest>;

// ── DTOs persistidos (lidos das duas tabelas da camada; nunca conteúdo de módulo) ───────────────────────────────
export const JOURNEY_VIEWS = ['ativas', 'concluidas', 'arquivadas', 'todas'] as const;
export type JourneyView = (typeof JOURNEY_VIEWS)[number];
export interface JourneyRecordDto {
  id: string; type: JourneyType; definitionVersion: number; title: string; scope: JourneyScope; scopeKey: string;
  lifecycle: JourneyLifecycle; lifecycleBeforeArchive: JourneyLifecycle | null; stepOverrides: StepOverrides;
  ownerUserId: string | null; ownerName: string | null;
  startedAt: string; pausedAt: string | null; completedAt: string | null; trackingSince: string | null; cancelledAt: string | null; archivedAt: string | null;
  closingJustification: string | null; version: number; createdBy: string | null; createdAt: string; updatedAt: string;
}
export interface JourneyListItemDto {
  id: string; type: JourneyType; title: string; lifecycle: JourneyLifecycle; startedAt: string; updatedAt: string; ownerName: string | null; version: number;
}
export interface JourneyLinkDto {
  id: string; stepId: StepId | null; relation: JourneyLinkRelation; entityType: string; entityId: string; entityVersionNo: number | null;
  reuse: JourneyLinkReuse; justification: string | null; snapshot: { label?: string; at?: string | null; status?: string };
  active: boolean; createdAt: string; createdByName: string | null; removedAt: string | null;
}
export interface JourneyHistoryItemDto {
  at: string; actor: string | null; action: string; label: string; stepId: StepId | null; source: 'JORNADA' | 'MODULO'; detail: Record<string, unknown>;
}

// ── Leitura agregada e catálogo (INTELIMOVE-JRN-006 — IM-API-316, IM-API-319) ─────────────────────────────────────
/** Títulos das cinco jornadas (MAPA_JORNADAS). Só as que têm definição publicada podem ser iniciadas. */
export const JOURNEY_TYPE_TITLE: Record<JourneyType, string> = {
  PLANEJAMENTO_ESTRATEGICO: 'Planejar a estratégia B2B', CONCORRENTES: 'Conhecer e acompanhar concorrentes', CARTEIRA_ALVOS: 'Encontrar e construir uma carteira de empresas-alvo',
  PORTFOLIO_POSICIONAMENTO: 'Rever portfólio, ofertas e posicionamento', ACOMPANHAMENTO_PLANO: 'Acompanhar o plano e corrigir a rota',
};
/** Rótulo dos tipos de item vinculável (exibição; nunca nome de tabela na tela). */
export const JOURNEY_ENTITY_LABEL: Record<JourneyLinkEntityType, string> = {
  company_context_version: 'Contexto confirmado', company_offering: 'Oferta', portfolio_analysis: 'Análise de portfólio', price_series: 'Preço', ecosystem_relation: 'Relação de ecossistema',
  battlecard: 'Battlecard', company_competitor: 'Concorrente', market_analysis: 'Análise de mercado', segmentation_analysis: 'Segmentação', market_sizing_analysis: 'TAM, SAM e SOM',
  environment_analysis: 'PESTEL e Cinco Forças', scenario_set: 'Simulação de cenários', strategic_record: 'Decisão', executive_report: 'Parecer', target_company: 'Empresa-alvo',
  person: 'Pessoa', source: 'Fonte', signal: 'Sinal', commercial_map: 'Mapa comercial', commercial_result: 'Resultado comercial', learning_report: 'Aprendizado', crm_delivery: 'Entrega ao CRM',
  competitor_research_run: 'Pesquisa de concorrente', target_research_run: 'Pesquisa de empresa-alvo', target_suggestion_run: 'Sugestão de empresas', market_run: 'Execução de mercado', people_run: 'Busca de pessoas',
};
export const JOURNEY_LINK_RELATION_LABEL: Record<JourneyLinkRelation, string> = { RESULTADO: 'Resultado da etapa', EXECUCAO: 'Execução vinculada', DECISAO: 'Decisão', INSUMO: 'Insumo reaproveitado' };
/** Situação de um vínculo resolvido no módulo dono no momento da leitura (referência suave). */
export const JOURNEY_LINK_AVAILABILITY = ['ATUAL', 'VERSAO_NOVA', 'HISTORICO', 'INDISPONIVEL'] as const;
export type JourneyLinkAvailability = (typeof JOURNEY_LINK_AVAILABILITY)[number];
export const JOURNEY_LINK_AVAILABILITY_LABEL: Record<JourneyLinkAvailability, string> = {
  ATUAL: 'Atual', VERSAO_NOVA: 'Há versão mais nova', HISTORICO: 'Histórico (arquivado no módulo)', INDISPONIVEL: 'Indisponível',
};
export interface JourneyResolvedLinkDto extends JourneyLinkDto {
  availability: JourneyLinkAvailability;
  /** Versão atual no módulo dono (para comparar com a versão vinculada); `null` quando indisponível ou sem versão. */
  currentVersionNo: number | null;
}
export interface JourneyStepViewDto extends DerivedStep {
  title: string; kind: JourneyStepDefinition['kind']; phase: JourneyStepDefinition['phase']; dependsOn: readonly StepId[]; skippable: boolean;
  completion: StepCompletion; doneWhen: string; output: string;
  /** Deep link para a tela existente responsável (artefato vigente, ou a alternativa sem artefato). */
  href: string | null; actionLabel: string | null;
}
export interface JourneyDto {
  journey: JourneyRecordDto;
  definition: { type: JourneyType; version: number; title: string; objective: string; completion: string; entry: string; tracking: string };
  state: JourneyStateView;
  steps: JourneyStepViewDto[];
  progress: JourneyProgress;
  pending: PendingItem[];
  nextAction: NextAction | null;
  completion: CompletionCheck;
  links: JourneyResolvedLinkDto[];
  /** Ao retomar: etapas cujo resultado mudou depois da pausa. */
  changedSincePause: StepId[];
  /** Progresso por item do escopo (J2: por concorrente — ESPECIFICACAO §IM-JRN-002.8), lido nos módulos donos; ausente na J4. */
  subjects?: JourneySubjectDto[];
  asOf: string;
}
/** Situação de um item do escopo numa etapa (texto sempre presente; nunca só cor). */
export const SUBJECT_STEP_STATES = ['OK', 'PENDENTE', 'EM_ANDAMENTO', 'ATENCAO', 'FALHOU', 'NAO_SE_APLICA'] as const;
export type SubjectStepState = (typeof SUBJECT_STEP_STATES)[number];
export interface JourneySubjectDto {
  id: string;
  label: string;
  /** Situação do item no módulo dono, já em linguagem de tela (ex.: "Concorrente confirmado"). */
  statusLabel: string;
  steps: Array<{ stepId: StepId; state: SubjectStepState; label: string }>;
  /** Tela existente do item (deep link). */
  href: string;
}
export interface JourneyCatalogItemDto {
  type: JourneyType; title: string;
  /** `false` = ainda sem definição publicada ("em breve"): não pode ser iniciada. */
  available: boolean;
  version: number | null; objective: string | null; needs: string | null; deliverable: string | null;
  steps: Array<{ id: StepId; title: string; kind: JourneyStepDefinition['kind']; phase: JourneyStepDefinition['phase'] }>;
  /** Estado do tipo no catálogo (sem instância viva); `null` quando indisponível. */
  state: (typeof JOURNEY_CATALOG_STATES)[number] | null;
  missing: readonly MissingItem[];
  live: Array<{ id: string; lifecycle: JourneyLifecycle; title: string; updatedAt: string }>;
}
